import { RecommendationProjectTypeEnum } from '../../../types';

/**
 * Business logic utilities following Clean Code principle: Pure functions
 * These functions have no side effects and are easily testable
 */

/**
 * Determines if there are API errors in both recommendation and carbon project data
 * Following Clean Code principle: Self-documenting function name
 */
export const hasProjectApiErrors = (
  recommendedError: string | Error | null,
  carbonError: string | Error | null,
): boolean => {
  return !!recommendedError && !!carbonError;
};

/**
 * Selects the appropriate recommended projects based on recommendation type
 * Following Clean Code principle: Pure function with clear input/output
 */
export const getCurrentRecommendedProjects = (
  selectedRecommendation: RecommendationProjectTypeEnum | undefined,
  forestationProjects: any[],
  recommendedProjects: any[],
): any[] => {
  return selectedRecommendation === RecommendationProjectTypeEnum.Forests
    ? forestationProjects
    : recommendedProjects;
};

/**
 * Determines loading state for recommended projects based on recommendation type
 * Following Clean Code principle: Single responsibility
 */
export const getRecommendedLoadingState = (
  selectedRecommendation: RecommendationProjectTypeEnum | undefined,
  forestationLoading: boolean,
  recommendedLoading: boolean,
): boolean => {
  return selectedRecommendation === RecommendationProjectTypeEnum.Forests
    ? forestationLoading
    : recommendedLoading;
};

/**
 * Determines loading state for project grid rendering
 * Following Clean Code principle: Clear parameter naming
 */
export const getProjectGridLoadingState = (
  selectedProject: string,
  carbonLoading: boolean,
  recommendedLoading: boolean,
): boolean => {
  switch (selectedProject) {
    case 'current':
      return carbonLoading;
    case 'recommendation':
      return recommendedLoading;
    default:
      return false;
  }
};

/**
 * Generates user-friendly message for empty recommendation state
 * Following Clean Code principle: Descriptive function name
 */
export const getEmptyRecommendationMessage = (
  selectedRecommendationType: RecommendationProjectTypeEnum | undefined,
): { mainMessage: string; subMessage: string } => {
  const projectType =
    selectedRecommendationType === RecommendationProjectTypeEnum.Forests ? 'forest' : 'mangrove';

  const alternativeType =
    selectedRecommendationType === RecommendationProjectTypeEnum.Forests ? 'Mangroves' : 'Forests';

  return {
    mainMessage: `No ${projectType} recommendations found`,
    subMessage: `Try switching to ${alternativeType} recommendations or adjusting your search criteria`,
  };
};

/**
 * Formats error message for display
 * Following Clean Code principle: Error handling at appropriate level
 */
export const formatErrorMessage = (
  recommendedError: string | Error | null,
  carbonError: string | Error | null,
): string => {
  return (
    (recommendedError as any)?.message ||
    recommendedError ||
    (carbonError as any)?.message ||
    carbonError ||
    'An error occurred'
  );
};

/**
 * Calculates total items for pagination based on selected project type
 * Following Clean Code principle: Pure function for business logic
 */
export const calculatePaginationTotalItems = (
  selectedProject: string,
  selectedRecommendation: RecommendationProjectTypeEnum | undefined,
  forestationTotals: number | undefined,
  paginationTotal: number,
  searchedCount: number | undefined,
): number => {
  if (selectedProject === 'recommendation') {
    return selectedRecommendation === RecommendationProjectTypeEnum.Forests
      ? forestationTotals || 0
      : paginationTotal;
  }
  return searchedCount || 0;
};
